import { HttpStatusCode } from '@angular/common/http';
import { Component, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Diagnostic } from '@awesome-cordova-plugins/diagnostic/ngx';
import { faKeyboard } from '@fortawesome/pro-regular-svg-icons';
import { faMicrophone as faMicrophoneReg } from '@fortawesome/pro-regular-svg-icons';
import { faMicrophone, faRepeat, faClose, faPaperPlaneTop } from '@fortawesome/pro-solid-svg-icons';
import { Platform } from '@ionic/angular';

import { catchError, distinctUntilChanged, map, of } from 'rxjs';

import { WaveConfigOptions } from '@empathy/audio';
import { createTurnId } from '@empathy/survey/base/turn-id';

import { AiChatAudioAnswer } from '../../../../models/ai-chat-audio-answer.interface';
import { AIChatType } from '../../../../models/ai-chat-type.enum';
import { SurveyViewerService } from '../../../../services/survey-viewer.service';

@Component({
  selector: 'empathy-keyboard-audio',
  templateUrl: 'keyboard-audio.component.html',
  styleUrls: ['./keyboard-audio.component.scss']
})
export class KeyboardAudioComponent implements OnInit {
  @Input() allowVoiceResponses = true;
  @Input() questionId: string;

  @Output() answerAudio = new EventEmitter<AiChatAudioAnswer>();
  @Output() answerText = new EventEmitter<string>();
  @Output() processing = new EventEmitter<boolean>();
  @Output() transcribeError = new EventEmitter<void>();

  processing$ = signal(false);
  stateType$ = signal<AIChatType>(AIChatType.Recorder);
  transcriptionFailed$ = signal(false);
  userTyped$ = signal(false);

  aIChatType = AIChatType;
  answerCtrl = new FormControl('', Validators.required);
  audioSettings: WaveConfigOptions = {
    autoScroll: true,
    autoCenter: true,
    dragToSeek: true,
    fillParent: true,
    barGap: 2,
    barRadius: 10,
    barWidth: 2,
    cursorColor: 'rgba(172, 212, 52, 1)',
    cursorWidth: 2,
    height: 24,
    normalize: false,
    progressColor: 'rgb(23, 92, 211)',
    waveColor: 'rgba(56, 58, 62, 0.7)'
  };

  faClose = faClose;
  faKeyboard = faKeyboard;
  faMicrophone = faMicrophone;
  faMicrophoneReg = faMicrophoneReg;
  faPaperPlaneTop = faPaperPlaneTop;
  faRepeat = faRepeat;

  private diagnostic = inject(Diagnostic);
  private platform = inject(Platform);
  private snackbar = inject(MatSnackBar);
  private surveyViewerService = inject(SurveyViewerService);

  private lastAudio: { audio: Blob; turnId: string } | null = null;

  ngOnInit(): void {
    if (!this.allowVoiceResponses) {
      this.stateType$.set(AIChatType.Keyboard);
    }

    this.answerCtrl.valueChanges.pipe(distinctUntilChanged()).subscribe((answer) => {
      this.userTyped$.set(!!answer);
    });
  }

  onAnswer(): void {
    this.lastAudio = null;
    this.transcriptionFailed$.set(false);

    if (!this.answerCtrl.valid || this.answerCtrl.value.trim().length === 0) {
      return;
    }

    this.answerText.emit(this.answerCtrl.value);
    this.answerCtrl.reset();
  }

  onAudioRecorderError(): void {
    this.showNotification('Failed to get microphone access, please use the keyboard');
    this.stateType$.set(AIChatType.Keyboard);
  }

  onAnswerAudio(audio: Blob): void {
    this.lastAudio = { audio, turnId: createTurnId() };
    this.transcriptionFailed$.set(false);
    this.processing$.set(true);
    this.processing.emit(true);

    this.uploadAudio();
  }

  onResendAudio(): void {
    if (!this.lastAudio) {
      return;
    }

    this.transcriptionFailed$.set(false);
    this.processing$.set(true);
    this.processing.emit(true);
    this.uploadAudio();
  }

  onDismiss(): void {
    this.lastAudio = null;
    this.transcriptionFailed$.set(false);
    this.stateType$.set(AIChatType.Recorder);
  }

  onEnableStartRecordingLayout(): void {
    if (!this.platform.is('mobile') || this.platform.is('mobileweb')) {
      this.stateType$.set(AIChatType.Recording);
    } else {
      this.diagnostic
        .isMicrophoneAuthorized()
        .then((authorized) => {
          if (authorized) {
            this.stateType$.set(AIChatType.Recording);
          } else {
            this.diagnostic
              .requestMicrophoneAuthorization()
              .then((status) => {
                if (status === this.diagnostic.permissionStatus.GRANTED) {
                  this.stateType$.set(AIChatType.Recording);
                } else {
                  this.showNotification('The application has no permissions to access the microphone');
                }
              })
              .catch(() => {
                this.showNotification('Failed to get microphone access, please use the keyboard');
                this.stateType$.set(AIChatType.Keyboard);
              });
          }
        })
        .catch(() => {
          this.showNotification('Failed to get microphone access, please use the keyboard');
          this.stateType$.set(AIChatType.Keyboard);
        });
    }
  }

  private uploadAudio(): void {
    const pendingAudio = this.lastAudio;
    if (!pendingAudio) {
      return;
    }

    this.surveyViewerService
      .uploadAiChatAudio(this.questionId, pendingAudio.turnId, pendingAudio.audio)
      .pipe(
        map((response) => response.audioReference),
        catchError((error) => {
          if (error.status === HttpStatusCode.Unauthorized) {
            this.transcribeError.emit();
          }
          return of(null);
        })
      )
      .subscribe((audioReference) => {
        this.processing$.set(false);
        this.processing.emit(false);

        if (!audioReference) {
          this.transcriptionFailed$.set(true);
          return;
        }

        this.answerAudio.emit({ audio: pendingAudio.audio, audioReference, turnId: pendingAudio.turnId });
        this.lastAudio = null;
        this.stateType$.set(AIChatType.Recorder);
      });
  }

  private showNotification(notification: string): void {
    this.snackbar.open(notification, 'Ok', { duration: 3000, panelClass: 'error' });
  }
}
